import { inject, injectable } from "tsyringe";
import { CRM_ACTIONS } from "src/containers/crm/constants/actions";
import { CrmRepository } from "src/containers/crm/repositories/crm/crm-repository";
import { autoBind } from "src/shared/decorators/autobind";

type PolicyIdProp = string | number | undefined;
type ClaimNumberProp = string;

type PolicyInput = { systemCode: string; policyId: PolicyIdProp };
type PlanInput = { productCode: string };
export type CrmInput = PolicyInput | PlanInput;

@injectable()
export class CrmService {
    constructor(@inject("CrmRepository") private repo: CrmRepository) {}

    // Maps fields correctly for CRM service
    private getCorrectedInput(input: CrmInput) {
        return {
            systemCode: (input as PolicyInput).systemCode,
            policyId: (input as PolicyInput).policyId,
            product: (input as PlanInput).productCode,
        };
    }

    @autoBind
    trackHRTDownloadRejection(input: CrmInput) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HEALTH_REIMBURSEMENT_TRACKING_DOWNLOAD_REJECTION,
            ...this.getCorrectedInput(input),
        });
    }

    @autoBind
    trackHRTDownloadLiquidation(input: CrmInput) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HEALTH_REIMBURSEMENT_TRACKING_DOWNLOAD_LIQUIDATION,
            ...this.getCorrectedInput(input),
        });
    }

    @autoBind
    loginSuccess() {
        return this.repo.saveTransaction({ action: CRM_ACTIONS.LOGIN_SUCCESS });
    }

    @autoBind
    createUser() {
        return this.repo.saveTransaction({ action: CRM_ACTIONS.CREATE_USER });
    }

    @autoBind
    changePassword() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.CHANGE_PASSWORD,
        });
    }

    @autoBind
    getAbcInsurance() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_ABC_INSURANCE,
        });
    }

    @autoBind
    listCoverages(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_COVERAGES,
            systemCode,
            policyId,
        });
    }

    @autoBind
    listClinics(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_CLINICS,
            systemCode,
            policyId,
        });
    }

    @autoBind
    listGarages(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_GARAGES,
            systemCode,
            policyId,
        });
    }

    @autoBind
    claimDetail(claimNumber: ClaimNumberProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.CLAIM_DETAIL,
            claimNumber,
        });
    }

    /**
     * @param systemCode Should follow the same rules for `policyId`
     * @param policyId Should be *only* used with any policy that is **not** EPS
     * @param productCode Should be *only* used with EPS plans
     */
    @autoBind
    getPolicyDetail(
        systemCode?: string,
        policyId?: PolicyIdProp,
        productCode?: string
    ) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_POLICY_DETAIL,
            systemCode,
            policyId,
            product: productCode,
        });
    }

    @autoBind
    listInsurances() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_INSURANCES,
        });
    }

    /**
     * @param systemCode Should follow the same rules for `policyId`
     * @param policyId Should be *only* used with any policy that is **not** EPS
     * @param productCode Should be *only* used with EPS plans
     */
    @autoBind
    downloadPolicy(
        systemCode?: string | null,
        policyId?: PolicyIdProp | null,
        productCode?: string
    ) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_POLICY,
            systemCode,
            policyId,
            product: productCode,
        });
    }

    @autoBind
    listCrossPaymentCoupons() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_CROSS_PAYMENT_COUPONS,
        });
    }

    @autoBind
    listPaymentCoupons(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LIST_PAYMENT_COUPONS,
            systemCode,
            policyId,
        });
    }

    @autoBind
    getVoucher(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VOUCHER,
            systemCode,
            policyId,
        });
    }

    @autoBind
    getInsuranceActivation(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_INSURANCE_ACTIVATION,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadSoat(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_SOAT,
            systemCode,
            policyId,
        });
    }

    /**
     * @param systemCode Should follow the same rules for `policyId`
     * @param policyId Should be *only* used with any policy that is **not** EPS
     * @param productCode Should be *only* used with EPS plans
     */
    @autoBind
    downloadCertificate(
        systemCode: string,
        policyId: PolicyIdProp,
        productCode: string
    ) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_CERTIFICATE,
            systemCode,
            policyId,
            product: productCode,
        });
    }

    @autoBind
    downloadEecc(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_EECC,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadBullet(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_BULLET,
            systemCode,
            policyId,
        });
    }

    @autoBind
    getPkTracking(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_PK_TRACKING,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadWellbeingMultiventajasPdf() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_MULTIVENTAJA,
        });
    }

    @autoBind
    getLifeBenefits() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_LIFE_BENEFITS,
        });
    }

    @autoBind
    getCarBenefits() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_CAR_BENEFITS,
        });
    }

    @autoBind
    downloadBallots(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_BALLOTS,
            systemCode,
            policyId,
        });
    }

    @autoBind
    claimRequest(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HOME_CLAIM_REQUEST,
            systemCode,
            policyId,
        });
    }

    @autoBind
    getHealthTravelerCarnetDetail(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_HEALTH_TRAVELER_CARNET_DETAIL,
            systemCode,
            policyId,
        });
    }

    @autoBind
    downloadHealthTravelerCarnet(systemCode: string, policyId: PolicyIdProp) {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.DOWNLOAD_HEALTH_TRAVELER_CARNET,
            systemCode,
            policyId,
        });
    }

    @autoBind
    getVeraWhatsappChat() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VERA_WHATSAPP_CHAT,
        });
    }

    @autoBind
    playScratchGame() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.PLAY_SCRATCH_GAME,
        });
    }

    @autoBind
    trackingDigitalRequest() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.TRACKING_DIGITAL_REQUEST,
        });
    }

    @autoBind
    medicineDeliveryRequestTracking() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.MEDICINE_DELIVERY_REQUEST_TRACKING,
        });
    }

    @autoBind
    getVehicleFines() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VEHICLE_FINES,
        });
    }

    @autoBind
    getVaccineCarnet() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.GET_VACCINE_CARNET,
        });
    }

    @autoBind
    vehicularAssistancesTrackRequest(
        action: CRM_ACTIONS,
        systemCode: string,
        policyId: PolicyIdProp
    ) {
        return this.repo.saveTransaction({
            action,
            systemCode,
            policyId,
        });
    }

    @autoBind
    accessElectionsOfficer() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.ELECTIONS_OFFICER,
        });
    }

    @autoBind
    coverNoteRequestTracking() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.COVER_NOTE_REQUEST_TRACKING,
        });
    }

    @autoBind
    healthReimbusementTrackingListing() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.HEALTH_REIMBURSEMENT_TRACKING_LISTING,
        });
    }

    @autoBind
    accessLocateYourStation() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.LOCATE_YOUR_STATION,
        });
    }

    @autoBind
    accessCocosYLucas() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.COCOS_Y_LUCAS,
        });
    }

    @autoBind
    accessPodcastEarthquake() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.PODCAST_EARTHQUAKE,
        });
    }

    @autoBind
    accessBenefitsVehicle() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.BENEFITS_VEHICLE,
        });
    }

    @autoBind
    accessReconocerte() {
        return this.repo.saveTransaction({
            action: CRM_ACTIONS.BENEFITS_RECONOCERTE,
        });
    }
}
